import { ArrowRightIcon } from "@/icons/ArrowRightIcon";
import type {
  DashboardNextShift,
  DashboardWeekStats,
  DashboardYesterdayStats,
} from "@/domain/dashboard/types";
import styles from "./DashboardDigest.module.css";

interface DashboardDigestProps {
  readonly today: string;
  readonly nextShift: DashboardNextShift | null;
  readonly weekStats: DashboardWeekStats | null;
  readonly yesterdayStats: DashboardYesterdayStats | null;
}

const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function toUtcDate(dateStr: string): Date {
  return new Date(`${dateStr}T12:00:00Z`);
}

function daysBetween(fromIso: string, toIso: string): number {
  const from = toUtcDate(fromIso).getTime();
  const to = toUtcDate(toIso).getTime();
  return Math.round((to - from) / (1000 * 60 * 60 * 24));
}

function relativeDay(today: string, target: string): string {
  const diff = daysBetween(today, target);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  const d = toUtcDate(target);
  const dayName = DAY_NAMES[d.getUTCDay()];
  if (diff > 1 && diff < 7) return dayName;
  if (diff >= 7 && diff < 14) return `Next ${dayName}`;
  return `${dayName}, ${d.getUTCDate()} ${MONTH_NAMES[d.getUTCMonth()]}`;
}

function formatTime(hhmm: string): string {
  const [hStr, mStr] = hhmm.split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  const period = h >= 12 ? "pm" : "am";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0 ? `${h12}${period}` : `${h12}:${mStr}${period}`;
}

function formatTimeRange(start: string, end: string): string {
  return `${formatTime(start)} - ${formatTime(end)}`;
}

function pluralise(n: number, singular: string, plural?: string): string {
  return `${n} ${n === 1 ? singular : (plural ?? `${singular}s`)}`;
}

function NextShiftHero({
  today,
  shift,
}: {
  readonly today: string;
  readonly shift: DashboardNextShift;
}) {
  const when = relativeDay(today, shift.date);
  return (
    <a href="/rota" className={styles.hero} aria-label={`Next shift: ${shift.siteName} ${when}`}>
      <span className={styles.heroLabel}>Coming up</span>
      <span className={styles.heroWhen}>{when}</span>
      <span className={styles.heroBody}>
        <span className={styles.heroTime}>{formatTimeRange(shift.startTime, shift.endTime)}</span>
        <span className={styles.heroDot} aria-hidden="true">·</span>
        <span className={styles.heroSite}>{shift.siteName}</span>
      </span>
      <span className={styles.heroCleaner}>{shift.cleanerName}</span>
      <span className={styles.heroArrow} aria-hidden="true">
        <ArrowRightIcon width={16} height={16} />
      </span>
    </a>
  );
}

function WeekGlance({ stats }: { readonly stats: DashboardWeekStats }) {
  if (stats.shiftCount === 0) {
    return (
      <a href="/rota" className={styles.glance} aria-label="Open rota to schedule shifts">
        <span className={styles.glanceLabel}>This week</span>
        <span className={styles.glanceHeadline}>Nothing scheduled yet</span>
        <span className={styles.glanceHint}>
          Open the rota
          <ArrowRightIcon width={14} height={14} />
        </span>
      </a>
    );
  }

  const parts: string[] = [];
  if (stats.siteCount > 0) parts.push(pluralise(stats.siteCount, "site"));
  if (stats.cleanerCount > 0) parts.push(pluralise(stats.cleanerCount, "cleaner"));

  return (
    <a href="/rota" className={styles.glance} aria-label="Open the rota">
      <span className={styles.glanceLabel}>This week</span>
      <span className={styles.glanceHeadline}>
        {pluralise(stats.shiftCount, "shift")}
      </span>
      <span className={styles.glanceHint}>
        {parts.join(" · ")}
        <ArrowRightIcon width={14} height={14} />
      </span>
    </a>
  );
}

function YesterdayGlance({
  stats,
}: {
  readonly stats: DashboardYesterdayStats;
}) {
  const headline =
    stats.visitCount === 0
      ? "No check-ins"
      : pluralise(stats.visitCount, "visit");
  const sub =
    stats.visitCount === 0
      ? stats.totalShifts > 0
        ? `of ${pluralise(stats.totalShifts, "shift", "shifts")} scheduled`
        : "nothing scheduled"
      : stats.onTimePercent !== null
        ? `${stats.onTimePercent}% on time`
        : "";

  return (
    <a href="/visits" className={styles.glance} aria-label="Open visits">
      <span className={styles.glanceLabel}>Yesterday</span>
      <span className={styles.glanceHeadline}>{headline}</span>
      <span className={styles.glanceHint}>
        {sub}
        <ArrowRightIcon width={14} height={14} />
      </span>
    </a>
  );
}

export function DashboardDigest({
  today,
  nextShift,
  weekStats,
  yesterdayStats,
}: DashboardDigestProps) {
  const hasAnything = nextShift || weekStats || yesterdayStats;
  if (!hasAnything) return null;

  return (
    <section className={styles.wrapper} role="region" aria-label="Dashboard digest">
      {nextShift ? (
        <NextShiftHero today={today} shift={nextShift} />
      ) : (
        <div className={styles.heroEmpty}>
          <span className={styles.heroLabel}>Coming up</span>
          <span className={styles.heroWhenMuted}>Nothing scheduled</span>
          <a href="/rota" className={styles.heroEmptyLink}>
            Open the rota
            <ArrowRightIcon width={14} height={14} />
          </a>
        </div>
      )}

      <div className={styles.glanceRow}>
        {weekStats && <WeekGlance stats={weekStats} />}
        {yesterdayStats && <YesterdayGlance stats={yesterdayStats} />}
      </div>
    </section>
  );
}
